// src/oura/components/docs/write/emailMarkup.ts
//
// The rich text editor's HTML back to the client email's markup (#4840), the
// one text the server previews, checks and sends (ourteam-backend#6700):
//
//   a paragraph            its lines, a blank line after it
//   <br>                   a line break inside the paragraph ("Cheers,\nAngel")
//   <strong>, <b>          **bold**
//   <a href="https://…">   [text](https://…), with \, [ and ] in the text escaped
//   <ul> / <ol>            "- item" / "1. item" lines, a blank line after the list
//
// Anything else the editor or a paste brings (italic, underline, colours,
// headings) keeps its words and drops its look: the email's renderer has none
// of them, so showing them in the editor would promise what the client never gets.

const BLOCKS = new Set(["p", "div", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "pre"]);

/** "**x**" with the spaces round it kept outside the marks, so the renderer reads it. */
function bold(inner: string): string {
    const m = /^(\s*)([\s\S]*?)(\s*)$/.exec(inner);
    if (!m || !m[2]) return inner;
    // Already bold inside (a bold link the server rendered): leave it as it is.
    if (/^\*\*[\s\S]*\*\*$/.test(m[2])) return inner;
    return `${m[1]}**${m[2]}**${m[3]}`;
}

/** A link's words, safe inside `[…]`: \, [ and ] escaped, as the server's `link_label` (Jack, #4841). */
function linkLabel(text: string): string {
    return text.replace(/([\\[\]])/g, "\\$1");
}

function inline(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) return (node.textContent ?? "").replace(/\u00a0/g, " ");
    if (!(node instanceof HTMLElement)) return "";
    const tag = node.tagName.toLowerCase();
    if (tag === "br") return "\n";
    const inner = Array.from(node.childNodes).map(inline).join("");
    if (tag === "a") {
        const href = (node.getAttribute("href") ?? "").trim();
        const text = inner.replace(/\s+/g, " ").trim();
        return /^https?:\/\//.test(href) && text ? `[${linkLabel(text)}](${href})` : inner;
    }
    if (tag === "strong" || tag === "b") return bold(inner);
    return inner;
}

/** A paragraph's lines, each trimmed, empty ones dropped. */
function paragraph(text: string): string {
    return text
        .split("\n")
        .map((l) => l.replace(/[ \t]+/g, " ").trim())
        .filter(Boolean)
        .join("\n");
}

export function htmlToMarkup(html: string): string {
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
    const root = doc.body.firstElementChild;
    if (!root) return "";
    const blocks: string[] = [];
    let loose = "";
    const flushLoose = () => {
        const p = paragraph(loose);
        if (p) blocks.push(p);
        loose = "";
    };
    for (const child of Array.from(root.childNodes)) {
        const tag = child instanceof HTMLElement ? child.tagName.toLowerCase() : "";
        if (tag === "ul" || tag === "ol") {
            flushLoose();
            const items = Array.from((child as HTMLElement).children)
                .filter((li) => li.tagName.toLowerCase() === "li")
                .map((li) => inline(li).replace(/\s+/g, " ").trim())
                .filter(Boolean);
            if (items.length) blocks.push(items.map((t, i) => (tag === "ul" ? `- ${t}` : `${i + 1}. ${t}`)).join("\n"));
        } else if (BLOCKS.has(tag)) {
            flushLoose();
            const p = paragraph(inline(child));
            if (p) blocks.push(p);
        } else {
            // Text or inline marks straight under the root: one paragraph until the next block.
            loose += inline(child);
        }
    }
    flushLoose();
    return blocks.join("\n\n");
}
